/**
 * CreatorSuggestionCard · iter152 (2026-02-24)
 *
 * OnlyFans-Style Redesign:
 *  - Cover-Bild füllt die komplette Karte (aspect 3:1, iter155 · vom User gefordert), NICHT abgeschnitten.
 *  - Runder Avatar links-mittig auf dem Cover.
 *  - Name mittig-unten in Weiß mit Drop-Shadow, kein @handle.
 *  - Kein „Kostenlos folgen"-Badge, kein „Profil"-Button — komplette Karte ist klickbar.
 *  - Verify-Badge & Online-Dot bleiben.
 *
 * Fallbacks:
 *  - Kein Cover → weicher Gradient
 *  - Kein Avatar → Initialen
 */
import { useNavigate } from "react-router-dom";
import { BadgeCheck } from "lucide-react";

export default function CreatorSuggestionCard({ creator, isStx = false }) {
  const navigate = useNavigate();
  const handle = creator.handle || creator.person_id;
  const name = creator.display_name || creator.handle || "Creator";
  const initials = (name || "?").slice(0, 2).toUpperCase();

  const goProfile = () => navigate(isStx ? `/user/${handle}/stx` : `/user/${handle}`);

  return (
    <button
      type="button"
      data-testid={`creator-suggest-card-${creator.person_id || handle}`}
      onClick={goProfile}
      className="relative block w-full rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group text-left"
      style={{ aspectRatio: "3 / 1" }}
    >
      {/* Cover · volle Fläche (iter278 · Creator-Card-Ausschnitt bevorzugt, Fallback Cover) */}
      {(creator.creator_card_url || creator.banner_url) ? (
        <img
          src={creator.creator_card_url || creator.banner_url}
          alt=""
          data-testid={`creator-suggest-cover-${creator.person_id || handle}`}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#333] to-[#666]" />
      )}

      {/* Gradient-Overlay unten für Text-Lesbarkeit */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/25 to-transparent pointer-events-none" />

      {/* Avatar · links, VERTIKAL ZENTRIERT, +20% größer (iter152.2 · 68px) */}
      <div className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border-2 border-white overflow-hidden bg-[#F5F5F5] shadow-lg"
           style={{ width: 68, height: 68 }}>
        {creator.avatar_url ? (
          <img src={creator.avatar_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sm font-bold text-[#666]">
            {initials}
          </div>
        )}
      </div>

      {/* Online-Dot am Avatar-Rand */}
      {creator.online === true && (
        <span
          data-testid={`creator-suggest-online-${creator.person_id || handle}`}
          className="absolute w-3 h-3 rounded-full bg-emerald-500 border-2 border-white"
          style={{ left: "74px", top: "calc(50% + 18px)" }}
          title="Online"
        />
      )}

      {/* iter165 · Name rechtsbündig mit dezentem Abstand — ragt nicht mehr ins Avatar-Bild
          (Avatar sitzt links bei ~80 px; wir reservieren mind. 88 px links).  */}
      <div className="absolute right-3 bottom-4 flex items-center gap-1 max-w-[calc(100%-92px)] pointer-events-none">
        <p
          className="text-sm font-black text-white truncate text-right"
          style={{ textShadow: "0 1px 3px rgba(0,0,0,0.75)" }}
        >
          {name}
        </p>
        {creator.verified && (
          <BadgeCheck size={14} className="text-white shrink-0"
            style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" }}
          />
        )}
      </div>

      {/* iter152: STX-Marker (optional) rechts oben klein, wenn im STX-Kontext */}
      {isStx && (
        <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider text-white/90 bg-black/40 backdrop-blur-sm px-1.5 py-0.5 rounded-full">
          18+
        </span>
      )}
    </button>
  );
}
